/**
 * E018-S007-T001: useBlockModelConfig Hook
 *
 * Reactive hook that reads AutomationConfig directly from BlockModel.
 * Subscribes to blockModel.propsUpdated to automatically re-extract config
 * on any prop change.
 *
 * This is the SINGLE SOURCE OF TRUTH for config - eliminates stale state.
 *
 * Architecture:
 * - BlockModel.props is the source
 * - This hook subscribes to changes
 * - ALL React components read through this hook
 * - No local useState<config> copies allowed
 */

import type { BlockModel } from '@blocksuite/affine/store';
import { useEffect, useState } from 'react';

import type {
  AutomationConfig,
  MatchConfigProps,
} from '../services/automation';

type ObservableTitle = {
  observe: (callback: () => void) => void;
  unobserve: (callback: () => void) => void;
};

function getObservableTitle(model: BlockModel): ObservableTitle | null {
  const title = (model.props as Record<string, unknown>).title as
    | { yText?: Partial<ObservableTitle> }
    | undefined;
  const yText = title?.yText;
  return yText &&
    typeof yText.observe === 'function' &&
    typeof yText.unobserve === 'function'
    ? (yText as ObservableTitle)
    : null;
}

// =============================================================================
// Helper Functions (moved from automation-panel.tsx for reuse)
// =============================================================================

/** Default empty filter group */
const emptyFilterGroup: AutomationConfig['filterGroup'] = {
  type: 'group',
  op: 'and',
  conditions: [],
};

/**
 * Helper to convert YArray/YMap to plain JS objects
 * YDoc stores arrays as YArray which need to be converted
 */
function toPlainValue<T>(value: unknown): T {
  if (value === null || value === undefined) {
    return value as T;
  }
  // Check for YArray (has toArray method)
  if (typeof (value as any)?.toArray === 'function') {
    const arr = (value as any).toArray();
    return arr.map((item: unknown) => toPlainValue(item)) as T;
  }
  // Check for YMap (has toJSON method and is not a plain object)
  if (
    typeof (value as any)?.toJSON === 'function' &&
    (value as any).constructor?.name !== 'Object'
  ) {
    return (value as any).toJSON() as T;
  }
  return value as T;
}

/**
 * Extract config from BlockModel props
 */
export function extractConfig(
  model: BlockModel | null
): AutomationConfig | null {
  if (!model) return null;

  const props = model.props as Record<string, unknown>;

  // Convert YArray/YMap values to plain JS
  const outputMappings = toPlainValue<AutomationConfig['outputMappings']>(
    props.outputMappings
  );
  const selectedColumns = toPlainValue<string[]>(props.selectedColumns);
  const filterGroup = toPlainValue<AutomationConfig['filterGroup']>(
    props.filterGroup
  );

  // Also extract statusTracking
  const statusTracking = toPlainValue<AutomationConfig['statusTracking']>(
    props.statusTracking
  );
  const batchConfig = toPlainValue<AutomationConfig['batchConfig']>(
    props.batchConfig
  );
  // E007-T022: Extract inputMappings for schema-based mapping
  const inputMappings = toPlainValue<AutomationConfig['inputMappings']>(
    props.inputMappings
  );
  // sl-m379: Extract inputTables for multi-input
  const inputTables = toPlainValue<AutomationConfig['inputTables']>(
    props.inputTables
  );
  const workflowSteps = toPlainValue<AutomationConfig['workflowSteps']>(
    props.workflowSteps
  );

  // sl-mau5: Extract matchConfigs
  const matchConfigs = toPlainValue<MatchConfigProps[]>(props.matchConfigs);

  return {
    title: props.title ? String(props.title) : null,
    titleMode:
      props.titleMode === 'custom' || props.titleMode === 'auto'
        ? props.titleMode
        : undefined,
    sourceTableId: (props.sourceTableId as string) ?? null,
    sourceDocId: (props.sourceDocId as string) ?? null, // E018-S014: Cross-Doc Support
    sourceTableName: (props.sourceTableName as string) ?? null,
    filterGroup: filterGroup ?? emptyFilterGroup,
    selectedColumns: selectedColumns ?? [],
    workflowId: (props.workflowId as string) ?? null,
    workflowName: (props.workflowName as string) ?? null,
    workflowSteps: workflowSteps ?? [],
    workflowStepsSourceId: (props.workflowStepsSourceId as string) ?? null,
    webhookUrlOverride: (props.webhookUrlOverride as string) ?? null,
    outputTables:
      (props.outputTables as AutomationConfig['outputTables']) ?? [],
    outputMappings: outputMappings ?? [],
    matchConfigs: matchConfigs ?? [],
    triggerConfig:
      (props.triggerConfig as AutomationConfig['triggerConfig']) ?? undefined,
    statusTracking: statusTracking ?? undefined,
    batchConfig: batchConfig ?? undefined,
    inputMappings: inputMappings ?? undefined,
    inputTables: inputTables ?? undefined,
  };
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * useBlockModelConfig - Reactive config from BlockModel
 *
 * Subscribes to blockModel.propsUpdated and automatically re-extracts
 * config on any prop change. This is the SINGLE SOURCE OF TRUTH.
 *
 * @param blockModel - The BlockModel to read config from
 * @returns AutomationConfig or null if no blockModel
 */
export function useBlockModelConfig(
  blockModel: BlockModel | null
): AutomationConfig | null {
  // Internal state - only updated via subscription
  const [config, setConfig] = useState<AutomationConfig | null>(() =>
    extractConfig(blockModel)
  );

  useEffect(() => {
    if (!blockModel) {
      setConfig(null);
      return;
    }

    const refreshConfig = () => setConfig(extractConfig(blockModel));
    let observedTitle: ObservableTitle | null = null;

    const syncTitleObserver = () => {
      const nextTitle = getObservableTitle(blockModel);
      if (nextTitle === observedTitle) return;
      observedTitle?.unobserve(refreshConfig);
      observedTitle = nextTitle;
      observedTitle?.observe(refreshConfig);
    };

    refreshConfig();
    syncTitleObserver();

    const subscription = blockModel.propsUpdated.subscribe(() => {
      // A title prop replacement needs a fresh Y.Text observer. Changes to
      // the existing Y.Text do not emit propsUpdated and are handled by the
      // observer itself.
      syncTitleObserver();
      refreshConfig();
    });

    return () => {
      subscription.unsubscribe();
      observedTitle?.unobserve(refreshConfig);
    };
  }, [blockModel]);

  return config;
}
